import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const opio: ChauffeurPage = {
  slug: "opio",
  title: "Chauffeur privé à Opio – VTC & transferts",
  subtitle:
    "Chauffeur privé et VTC à Opio : transfert aéroport de Nice, golf de la Grande Bastide et oliveraies, dans l’arrière-pays de Valbonne.",
  keywords: [
    "chauffeur privé opio",
    "vtc opio",
    "chauffeur opio",
    "transfert opio aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Opio",
    subtitle:
      "Le village des oliveraies et du golf de la Grande Bastide, dans l’arrière-pays de Valbonne : transferts aéroport de Nice et mises à disposition.",
    keywords: [
      "VTC Opio",
      "chauffeur golf Grande Bastide",
      "transfert aéroport Nice",
      "chauffeur arrière-pays Valbonne",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Opio",
    badges: [
      "Golf & oliveraies",
      "Arrière-pays Valbonne",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Opio",
    items: [
      {
        title: "Golf de la Grande Bastide",
        description:
          "Transferts vers le golf et les parcours voisins, avec place pour les sacs et horaires calés sur votre départ, sans souci de stationnement.",
      },
      {
        title: "Oliveraies & art de vivre",
        description:
          "Village d’oliviers et de moulins, à l’écart de l’agitation : un chauffeur dédié pour vos déplacements au rythme de l’arrière-pays.",
      },
      {
        title: "Aéroport de Nice ~40 min",
        description:
          "Rejoignez l’aéroport de Nice Côte d’Azur en environ 40 minutes par Valbonne et l’A8, avec suivi de vol.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, basés sur la Côte d’Azur, ponctuels et discrets — 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Opio",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge à Opio avec suivi de vol, vers et depuis l’aéroport de Nice Côte d’Azur, en environ 40 minutes.",
      },
      {
        title: "Transfert golf",
        text: "Liaison vers le golf de la Grande Bastide et les parcours de la région, avec espace bagages pour les sacs et timing calé sur le tee-off.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée : Valbonne, Grasse, Sophia Antipolis, moulins à huile et villages de l’arrière-pays.",
      },
      {
        title: "Liaison Sophia Antipolis",
        text: "Trajets vers la technopole et les entreprises voisines, pour rendez-vous d’affaires et déplacements professionnels.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Opio : Cannes, Nice, Monaco, Italie et stations de ski, en véhicule confortable.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Golf de la Grande Bastide",
        description:
          "Parcours d’Opio–Valbonne, départ et reprise calés sur le jeu.",
      },
      {
        name: "Village d’Opio",
        description: "Cœur du village, oliveraies et moulins à huile.",
      },
      {
        name: "Valbonne",
        description: "Village voisin à plan en damier, à quelques minutes.",
      },
      {
        name: "Sophia Antipolis",
        description: "Technopole toute proche, entreprises et laboratoires.",
      },
      {
        name: "Grasse",
        description: "Capitale du parfum, accessible par l’arrière-pays.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 40 minutes par Valbonne et l’A8.",
      },
    ],
  },
  generic: {
    heading: "Opio, oliveraies et golf de l’arrière-pays",
    subheading:
      "Un village d’oliviers et de golf, dans l’arrière-pays de Valbonne, au calme mais bien relié.",
    paragraphs: [
      "Opio cultive un art de vivre tranquille, entre oliveraies et moulins à huile, à l’écart de l’agitation du littoral. Son golf de la Grande Bastide en fait un rendez-vous des amateurs : le chauffeur assure la liaison avec espace pour les sacs et horaires calés sur le tee-off, sans contrainte de stationnement.",
      "Le village est aussi bien relié à l’arrière-pays actif : Valbonne, la technopole de Sophia Antipolis et Grasse sont à portée de route, pour un rendez-vous d’affaires comme pour une escapade. Nos chauffeurs composent l’itinéraire selon votre programme.",
      "Pour l’aéroport de Nice Côte d’Azur, comptez environ 40 minutes par Valbonne et l’autoroute A8 selon le trafic ; nos chauffeurs bilingues suivent votre vol et adaptent le départ.",
    ],
    bullets: [
      "Transfert golf avec sacs",
      "Liaison Sophia Antipolis & Valbonne",
      "Suivi de vol & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Opio",
    steps: [
      "Indiquez votre trajet (golf, Sophia Antipolis, aéroport de Nice) et vos horaires.",
      "Nous confirmons le véhicule adapté et le forfait fixe ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’adresse convenue.",
      "Ajustements en direct selon le trafic et votre départ au golf.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Opio"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Comptez environ 40 minutes par Valbonne et l’autoroute A8 selon le trafic, avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Assurez-vous les transferts vers le golf de la Grande Bastide ?",
      a: "Oui, avec un véhicule offrant l’espace bagages nécessaire pour les sacs et des horaires calés sur votre départ au tee-off, à Opio–Valbonne comme sur les parcours voisins.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et lors des événements de Sophia Antipolis ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Desservez-vous Sophia Antipolis et Valbonne depuis Opio ?",
      a: "Oui, la technopole et Valbonne sont à quelques minutes ; nous assurons transferts et mises à disposition pour vos rendez-vous d’affaires dans tout le secteur.",
    },
  ],
  relatedLinks: [
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Navette aéroport de Nice", href: "/chauffeur/aeroport" },
    {
      label: "Transferts avec chauffeur à Cannes",
      href: "/chauffeur/cannes",
    },
    {
      label: "Location de voiture à Grasse",
      href: "/location/location-voiture-grasse",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Opio",
    text: "Transfert aéroport de Nice, liaison golf ou mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Opio — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Opio : transfert aéroport de Nice en ~40 min, golf de la Grande Bastide, oliveraies et arrière-pays de Valbonne. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/opio",
  },
};

export default opio;
